import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import './CookieBanner.css';

const STORAGE_KEY = 'casa_cookie_consent_v1';
const API = process.env.REACT_APP_BACKEND_URL;

// Hide the banner on internal/admin surfaces — they aren't public pages.
const SUPPRESSED_PATHS = ['/admin'];

/**
 * Casa de Candy — cookie/analytics consent banner.
 *
 * Shows once per browser. Stores the decision locally and POSTs a
 * compliance record to the backend so we have an auditable trail.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();
  const suppressed = SUPPRESSED_PATHS.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (suppressed) {
      setVisible(false);
      return;
    }
    try {
      const decided = localStorage.getItem(STORAGE_KEY);
      if (!decided) {
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch (_e) {
      // Local storage blocked; surface the banner anyway so users can opt-in.
      setVisible(true);
    }
  }, [suppressed]);

  // When the banner is visible, mark the body so other floating UI
  // (e.g. the persistent "Book Session Now" CTA) can lift itself above it.
  // Also expose the actual measured banner height as a CSS variable so the
  // lift offset adapts to flex-wrapped mobile layouts.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;

    if (visible) {
      document.body.classList.add('cookie-banner-active');
    } else {
      document.body.classList.remove('cookie-banner-active');
      root.style.removeProperty('--cookie-banner-h');
    }

    if (!visible) return;

    const inner = document.querySelector('.cookie-banner__inner');
    if (!inner) return;

    const setVar = () => {
      const h = inner.getBoundingClientRect().height || 0;
      // Add the wrapper's outer padding (14px) so we clear the full banner.
      root.style.setProperty('--cookie-banner-h', `${Math.ceil(h + 28)}px`);
    };
    setVar();

    const ro = new ResizeObserver(setVar);
    ro.observe(inner);
    window.addEventListener('resize', setVar);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', setVar);
      document.body.classList.remove('cookie-banner-active');
      root.style.removeProperty('--cookie-banner-h');
    };
  }, [visible]);

  const decide = async (accepted) => {
    setSubmitting(true);
    try {
      localStorage.setItem(STORAGE_KEY, accepted ? 'accepted' : 'declined');
    } catch (_e) {
      /* ignore — still record server-side */
    }
    try {
      await fetch(`${API}/api/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          page: window.location.pathname,
          consent_version: 'v1.2026.02',
          cookie_consent: accepted,
          terms_accepted: false,
          age_confirmed: false,
        }),
      });
    } catch (_e) {
      /* network errors shouldn't block the UX */
    }
    setSubmitting(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="cookie-banner" role="dialog" aria-live="polite" data-testid="cookie-banner">
      <div className="cookie-banner__inner">
        <div className="cookie-banner__icon" aria-hidden>
          <Cookie className="w-5 h-5" />
        </div>
        <div className="cookie-banner__copy">
          <p>
            We use cookies and similar technologies only as needed to improve site
            functionality, security, scheduling, and user experience. By continuing,
            you agree to our{' '}
            <Link to="/privacy" className="cookie-banner__link">Privacy Policy</Link>.
          </p>
        </div>
        <div className="cookie-banner__actions">
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--ghost"
            onClick={() => decide(false)}
            disabled={submitting}
            data-testid="cookie-decline"
          >
            Decline
          </button>
          <button
            type="button"
            className="cookie-banner__btn cookie-banner__btn--primary"
            onClick={() => decide(true)}
            disabled={submitting}
            data-testid="cookie-accept"
          >
            Accept
          </button>
        </div>
        <button
          type="button"
          className="cookie-banner__dismiss"
          aria-label="Close"
          onClick={() => decide(false)}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

export default CookieBanner;
